import React, { useState, useEffect } from 'react';
import '../styles/Sidebar.css';
import { useAuth } from '../context/AuthContext';

const baseMenu = [
  { id: 'Dashboard', name: 'Dashboard', icon: '⊞' },
  {
    id: 'Master', name: 'Master', icon: '📋', hasChildren: true,
    children: [
      { id: 'Product', name: 'Product' },
      { id: 'Tank', name: 'Tank' },
      { id: 'PumpList', name: 'Pump' },
      { id: 'NozzleConfig', name: 'Nozzle' },
      { id: 'Supplier', name: 'Supplier' },
      { id: 'Customer', name: 'Customer' },
      { id: 'Employees', name: 'Employees', icon: '👷' },
    ]
  },
  // {
  //   id: 'Entry', name: 'Entry', icon: '✏️', hasChildren: true,
  //   children: [
  //     { id: 'OpeningStock', name: 'Opening Stock' },
  //     { id: 'ClosingStock', name: 'Closing Stock' },
  //   ]
  // },
  {
    id: 'Sale', name: 'Sales', icon: '💰', hasChildren: true,
    children: [
      { id: 'SaleList', name: 'Sale List' },
      { id: 'MeterReading', name: 'Meter Reading' },
      { id: 'LubeSales', name: 'Lube Sales' },
      { id: 'SaleBillLubeGST', name: 'Sale Bill [Lube-GST]' },
      { id: 'CreditSale', name: 'Credit Sale' },
      { id: 'DailyReceipt', name: 'Daily Receipt' },
      { id: 'BillReceipt', name: 'Bill Receipt' },
      { id: 'SaleReturn', name: 'Sale Return' },
    ]
  },
  { id: 'ItemLoss', name: 'Item Loss', icon: '📉' },
  {
    id: 'Purchase', name: 'Purchase', icon: '🛒', hasChildren: true,
    children: [
      { id: 'PurchaseOrder', name: 'Purchase' },
      { id: 'Expenses', name: 'Expenses' },
      { id: 'PurchaseReturn', name: 'Purchase Return' },
    ]
  },
  {
    id: 'Dip', name: 'Dip', icon: '📏', hasChildren: true,
    children: [
      { id: 'DipReading', name: 'Dip Reading' },
      { id: 'DipChart', name: 'Dip Chart' },
      { id: 'FuelBalance', name: 'Fuel Balancing' },
    ]
  },
  {
    id: 'Accounting', name: 'Accounting', icon: '📒', hasChildren: true,
    children: [
      { id: 'Accounting', name: 'Reports & Ledger' },
      { id: 'Ledgers', name: 'Chart of Accounts', icon: '📒' },
      { id: 'LedgerView', name: 'Ledger View', icon: '🔍' },
      { id: 'Payments', name: 'Payments' },
      { id: 'Contra', name: 'Contra Entry', icon: '↔️' },
      { id: 'Journal', name: 'Journal Voucher', icon: '📝' },
      { id: 'FinancialStatements', name: 'Financial Statements', icon: '📊' },
      { id: 'StockLedger', name: 'Stock Ledger', icon: '📊' },
      { id: 'Banking', name: 'Banking & Financials', icon: '🏦' },
      { id: 'Reconciliation', name: 'Day Reconciliation', icon: '⚖️' },
      { id: 'Overtime', name: 'Overtime', icon: '⏱️' },
    ]
  },
  { id: 'Support', name: 'Support & Tickets', icon: '🎫' },
  { id: 'RegisterStation', name: 'Register New Station', icon: '➕' },
];

// Admin-only items — appended conditionally
const adminMenu = [
  { id: 'AuditLog', name: 'Audit Log', icon: '🛡️' },
  { id: 'UserManagement', name: 'Users & Roles', icon: '👥' },
];

// Noris-team-only items (platform admins) — appended conditionally
const norisMenu = [
  { id: 'Approvals', name: 'Station Approvals', icon: '🛡️' },
];

function getParentId(childId, items) {
  for (const item of items) {
    if (item.hasChildren && item.children.some(c => c.id === childId)) {
      return item.id;
    }
  }
  return null;
}

export default function Sidebar({ activePage, setActivePage, mobileOpen, onClose }) {
  const [collapsed, setCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const { user, logout, isTrialExpired, isNorisTeam, canAccess } = useAuth();

  // Mapping of Sidebar ID -> Module Name (for permissions)
  const menuModuleMap = {
    'Dashboard': 'dashboard',
    'Product': 'products',
    'Tank': 'tanks',
    'PumpList': 'pumps',
    'NozzleConfig': 'pumps',
    'Supplier': 'suppliers',
    'Customer': 'customers',
    'Employees': 'employees',
    'SaleList': 'sales',
    'MeterReading': 'meter_readings',
    'LubeSales': 'lube_sales',
    'SaleBillLubeGST': 'lube_sales',
    'CreditSale': 'credit_sales',
    'DailyReceipt': 'daily_receipts',
    'BillReceipt': 'bill_receipts',
    'SaleReturn': 'sales',
    'ItemLoss': 'item_loss',
    'PurchaseOrder': 'purchases',
    'Expenses': 'expenses',
    'PurchaseReturn': 'purchases',
    'DipReading': 'dip_readings',
    'DipChart': 'dip_readings',
    'FuelBalance': 'fuel_balance',
    'Accounting': 'accounting',
    'Ledgers': 'accounting',
    'LedgerView': 'accounting',
    'Payments': 'payments',
    'Contra': 'accounting',
    'Journal': 'accounting',
    'FinancialStatements': 'financials',
    'StockLedger': 'stock_ledger',
    'Banking': 'banks',
    'Reconciliation': 'reconciliation',
    'Overtime': 'overtime',
    // Support has no backend RBAC gate (tickets.php only calls requireAuth) and
    // the manual keeps it always reachable. Leaving it unmapped makes filterMenu
    // fall through to "no module → always show" (same as Dashboard).
    'RegisterStation': 'stations',
    'AuditLog': 'audit_logs',
    'UserManagement': 'roles',
    'Approvals': 'stations'
  };

  const filterMenu = (items) => {
    return items
      .map(item => {
        if (item.hasChildren) {
          const visibleChildren = filterMenu(item.children);
          if (visibleChildren.length === 0) return null;
          return { ...item, children: visibleChildren };
        }
        const module = menuModuleMap[item.id];
        if (!module) return item; // Items without module mapping (e.g. Dashboard if not in DB)
        return canAccess(module) ? item : null;
      })
      .filter(Boolean);
  };

  const menuItems = [
    ...filterMenu(baseMenu),
    ...(isNorisTeam ? norisMenu : []),
    ...filterMenu(adminMenu),
  ];

  useEffect(() => {
    const parentId = getParentId(activePage, menuItems);
    if (parentId) {
      setOpenMenus(prev => ({
        ...prev,
        [parentId]: true
      })); // Accordion: only one menu open
    }
    // eslint-disable-next-line
  }, [activePage]);

  const toggleMenu = (id) => {
    setOpenMenus(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleClick = (item) => {
    if (isTrialExpired && item.id !== 'Dashboard' && item.id !== 'Support') return;
    if (item.hasChildren) {
      if (!collapsed) {
        toggleMenu(item.id);
      }
      return;
    } else {
      setActivePage(item.id);
    }
  };

  const isParentActive = (item) => {
    if (!item.hasChildren) return false;
    return item.children.some(c => c.id === activePage) || activePage === item.id;
  };

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-logo">
        <div className="logo-icon">
          <img src="https://noris.in/Assets/N%20logo.webp" alt="Noris"
            style={{ width: 28, height: 28, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <span style={{ display: 'none', color: 'white', fontWeight: 900, fontSize: 16 }}>N</span>
        </div>
        {!collapsed && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1, overflow: 'hidden' }}>
            <span className="logo-text">Noris Petro ERP</span>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.5px' }}>by noris.in</span>
          </div>
        )}
        <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? '▶' : '◀'}
        </button>
      </div>

      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          <div key={item.id}>
            <div
              className={`sidebar-item ${activePage === item.id || isParentActive(item) ? 'active' : ''} ${isTrialExpired && item.id !== 'Dashboard' && item.id !== 'Support' ? 'disabled' : ''
                }`}
              onClick={() => handleClick(item)}
            >
              <span className="item-icon">{item.icon}</span>
              {!collapsed && (
                <>
                  <span className="item-name">{item.name}</span>
                  {item.hasChildren && (
                    <span className="arrow" style={{
                      transform: openMenus[item.id] ? 'rotate(90deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.2s'
                    }}>
                      ‹
                    </span>
                  )}
                </>
              )}
            </div>

            {!collapsed && item.hasChildren && openMenus[item.id] && (
              <div className="sidebar-submenu">
                {item.children.map((child) => (
                  <div
                    key={child.id}
                    className={`sidebar-item ${activePage === child.id ? 'active' : ''} ${isTrialExpired && child.id !== 'Dashboard' && child.id !== 'Support' ? 'disabled' : ''
                      }`}
                    onClick={() => {
                      if (isTrialExpired && child.id !== 'Dashboard' && child.id !== 'Support') return;
                      setActivePage(child.id);
                    }}
                  >
                    <span className="item-icon">›</span>
                    <span className="item-name">{child.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-item logout-item" onClick={logout} title="Logout">
          <span className="item-icon">⏻</span>
          {!collapsed && <span className="item-name">Logout</span>}
        </div>
      </div>
    </div>
  );
}
